import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/pfc";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Radio, Lock } from "lucide-react";

const PLATFORMS = [
  { v: "youtube", l: "YouTube Live" },
  { v: "twitch", l: "Twitch" },
  { v: "instagram", l: "Instagram Live" },
  { v: "tiktok", l: "TikTok Live" },
  { v: "zoom", l: "Zoom" },
  { v: "other", l: "Other" },
];

export default function MyLivePanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [premiumCheck, setPremiumCheck] = useState({ has_premium: false, checked: false });

  const load = async () => {
    try {
      const [mine, creator] = await Promise.all([
        api.get("/live/mine"),
        api.get("/creator/me").catch(() => ({ data: {} })),
      ]);
      setItems(mine.data);
      setPremiumCheck({ has_premium: !!creator.data?.premium_active, checked: true });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this live appearance?")) return;
    try {
      await api.delete(`/live/mine/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="live-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl">My Live Appearances</h2>
          <p className="text-white/60 text-sm mt-1">Schedule live appearances that show up on the <a href="/live" className="text-pfc-gold underline">/live</a> page and your profile. Premium creators only.</p>
        </div>
        <button
          onClick={() => setEditing({ title: "", description: "", platform: "youtube", stream_url: "", cover_image: "", scheduled_at: "", ends_at: "", is_exclusive: false })}
          data-testid="add-live-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-xs hover:bg-pfc-gold-bright disabled:opacity-50"
        >
          <Plus size={14} /> Schedule Live
        </button>
      </div>

      {premiumCheck.checked && !premiumCheck.has_premium && (
        <div className="mt-5 border border-pfc-gold/30 bg-pfc-gold/5 p-4 flex items-start gap-3" data-testid="live-premium-prompt">
          <Lock className="text-pfc-gold mt-0.5" size={18} />
          <div className="text-sm text-white/80">
            Scheduling Live appearances requires <a href="/settings?tab=premium" className="text-pfc-gold underline">PFC Premium</a> ($9.99/mo or $199/yr) or an active PFC Community subscription.
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-white/40 py-12 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-white/40 py-12 text-center border border-dashed border-white/10 mt-6">No live appearances scheduled.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-testid="my-live-grid">
          {items.map((l) => (
            <div key={l.live_id} className="border border-white/10 bg-ink-800/30 p-4" data-testid={`live-row-${l.live_id}`}>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-sub mb-1">
                <Radio size={11} className={l.status === "live" ? "text-pfc-red animate-pulse" : "text-pfc-gold"} />
                <span className={l.status === "live" ? "text-pfc-red" : l.status === "upcoming" ? "text-pfc-gold" : "text-white/40"}>{l.status}</span>
                <span className="text-white/40">· {l.platform}</span>
                {l.is_exclusive && <span className="text-pfc-gold">· Exclusive</span>}
              </div>
              <div className="text-white font-sub">{l.title}</div>
              <div className="text-white/50 text-xs mt-1">{new Date(l.scheduled_at).toLocaleString()}</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(l)} className="px-3 py-1 border border-white/15 text-[11px] uppercase tracking-[0.2em] font-sub hover:bg-white/5" data-testid={`edit-live-${l.live_id}`}><Pencil size={11} className="inline" /> Edit</button>
                <button onClick={() => del(l.live_id)} className="px-3 py-1 border border-pfc-red/30 text-pfc-red text-[11px]" data-testid={`del-live-${l.live_id}`}><Trash2 size={11} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <LiveEditor live={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function LiveEditor({ live, onClose, onSaved }) {
  const toLocalInput = (iso) => {
    if (!iso) return "";
    try { const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); } catch { return ""; }
  };
  const [form, setForm] = useState({
    title: live.title || "",
    description: live.description || "",
    platform: live.platform || "youtube",
    stream_url: live.stream_url || "",
    cover_image: live.cover_image || "",
    scheduled_at: toLocalInput(live.scheduled_at) || "",
    ends_at: toLocalInput(live.ends_at) || "",
    is_exclusive: !!live.is_exclusive,
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.scheduled_at) { toast.error("Scheduled time is required."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      };
      if (live.live_id) {
        await api.put(`/live/mine/${live.live_id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/live/mine", payload);
        toast.success("Scheduled");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="live-editor">
      <div className="bg-ink-900 border border-pfc-gold/40 max-w-md w-full p-7">
        <div className="flex items-baseline justify-between mb-5">
          <h3 className="font-display text-2xl">{live.live_id ? "Edit Live" : "Schedule Live"}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white text-sm">Close</button>
        </div>
        <form onSubmit={save} className="space-y-3">
          <I label="Title" required value={form.title} onChange={(v) => upd("title", v)} testId="live-title" />
          <T label="Description" value={form.description} onChange={(v) => upd("description", v)} testId="live-desc" />
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">Platform</div>
            <select value={form.platform} onChange={(e) => upd("platform", e.target.value)} data-testid="live-platform" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none">
              {PLATFORMS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </label>
          <I label="Stream URL" required value={form.stream_url} onChange={(v) => upd("stream_url", v)} placeholder="https://youtube.com/live/..." testId="live-url" />
          <I label="Cover Image URL" value={form.cover_image} onChange={(v) => upd("cover_image", v)} testId="live-cover" />
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">Starts</div>
              <input type="datetime-local" required value={form.scheduled_at} onChange={(e) => upd("scheduled_at", e.target.value)} data-testid="live-start" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">Ends (optional)</div>
              <input type="datetime-local" value={form.ends_at} onChange={(e) => upd("ends_at", e.target.value)} data-testid="live-end" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none" />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={form.is_exclusive} onChange={(e) => upd("is_exclusive", e.target.checked)} data-testid="live-exclusive" /> Premium-only (stream URL hidden from non-subscribers)
          </label>
          <button type="submit" disabled={saving} data-testid="save-live-btn" className="w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

function I({ label, value, onChange, required, type = "text", placeholder, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}{required && <span className="text-pfc-red"> *</span>}</div>
      <input type={type} required={required} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none" />
    </label>
  );
}
function T({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}</div>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={testId} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none resize-y" />
    </label>
  );
}
